import axios from 'axios';

import { config } from '@/app/config';

import { CREDENTIAL_ENDPOINTS } from './endpoints';
import { toAppError } from './errors';

/**
 * The single axios instance. This module is the only place in the app allowed
 * to import axios (enforced by a `no-restricted-imports` ESLint rule);
 * everything else consumes the typed helpers and `AppError`.
 */

let authToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export const api = axios.create({
  baseURL: config.apiBaseUrl,
  headers: { Accept: 'application/json' },
  // Auth travels in the Authorization header, not a cookie. Sending credentials
  // would require the backend's CORS supports_credentials, which is off by
  // design — see config/cors.php.
  withCredentials: false,
  timeout: 30_000,
});

/** Sets (or clears) the bearer token sent with every subsequent request. */
export function setAuthToken(token: string | null): void {
  authToken = token;
}

/**
 * Registers the callback fired when the server rejects our token. AuthProvider
 * uses this to clear local auth state; routing then reacts to that state.
 *
 * Deliberately a callback rather than a `window.location` redirect: a hard
 * navigation would lose the "return here after signing in" location, discard
 * React state, and be untestable under jsdom.
 */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

api.interceptors.request.use((requestConfig) => {
  // Re-asserted per request rather than relying only on the instance defaults:
  // the backend's BaseResponse only serialises JSON when the request
  // `expectsJson()`, and otherwise falls through to a null Blade view and
  // throws. A dropped Accept header turns every success into a 500.
  requestConfig.headers.set('Accept', 'application/json');

  if (authToken) {
    requestConfig.headers.set('Authorization', `Bearer ${authToken}`);
  }

  return requestConfig;
});

/**
 * When a request sets `responseType: 'blob'` (downloads), an ERROR body arrives
 * as a Blob too — so the JSON envelope inside it is invisible to `toAppError`
 * and the user would get a generic "something went wrong" instead of the
 * server's actual message. Read it back to text and swap it in first.
 */
async function unwrapBlobErrorBody(rawError: unknown): Promise<void> {
  const response = (rawError as { response?: { data?: unknown } } | null)?.response;

  if (!response || typeof Blob === 'undefined' || !(response.data instanceof Blob)) return;

  try {
    const text = await response.data.text();
    response.data = JSON.parse(text);
  } catch {
    // Not JSON (a genuine binary error page, or an empty body). Leave it; the
    // status-derived fallback message in toAppError takes over.
    response.data = undefined;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (rawError: unknown) => {
    await unwrapBlobErrorBody(rawError);

    const error = toAppError(rawError);
    const url = (rawError as { config?: { url?: string } } | null)?.config?.url ?? '';

    if (error.status === 401 && !CREDENTIAL_ENDPOINTS.has(url)) {
      onUnauthorized?.();
    }

    // Reject with the normalised error so no caller ever sees an AxiosError.
    return Promise.reject(error);
  },
);
